import { useState } from 'react';
import { getRiskCategory } from '@/data/RiskCalculator';
import { Strings } from '@/constants/strings';
import { RiskGauge } from './RiskGauge';
import { LegendModal } from './LegendModal';
import { getRecommendationsByCategory } from './RecommendationsCard';
import { RecommendationsModal } from './RecommendationsModal';
import type { CalculationResult } from '@/data/RiskCalculator';
import type { UserMode } from './RiskCalculator';

function getCategoryLabel(cat: 'low' | 'moderate' | 'high' | 'veryHigh'): string {
  switch (cat) {
    case 'low':
      return Strings.riskLow;
    case 'moderate':
      return Strings.riskModerate;
    case 'high':
      return Strings.riskHigh;
    case 'veryHigh':
      return Strings.riskVeryHigh;
  }
}

const RECOMMENDATIONS_BUTTON_CLASS: Record<'low' | 'moderate' | 'high' | 'veryHigh', string> = {
  low: 'bg-risk-low hover:opacity-90 active:opacity-95',
  moderate: 'bg-risk-low hover:opacity-90 active:opacity-95',
  high: 'bg-risk-high hover:opacity-90 active:opacity-95',
  veryHigh: 'bg-risk-very-high hover:opacity-90 active:opacity-95',
};

interface InputResultProps {
  result: CalculationResult;
  userMode: UserMode;
  onNewCalculation: () => void;
}

export function InputResult({ result, userMode, onNewCalculation }: InputResultProps) {
  const [legendOpen, setLegendOpen] = useState(false);
  const [recommendationsModalOpen, setRecommendationsModalOpen] = useState(false);
  const category = getRiskCategory(result.riskPercent);
  const categoryLabel = getCategoryLabel(category);
  const isPublic = userMode === 'public';
  const recommendations = getRecommendationsByCategory(category);

  return (
    <div className="flex min-h-[calc(100vh-56px)] flex-1 flex-col bg-surface">
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="mx-auto flex max-w-md flex-col space-y-6">
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium leading-relaxed text-amber-900">
            {Strings.medicalDisclaimer}
          </div>

          <div className="overflow-hidden rounded-3xl border border-slate-100 bg-white/80 py-8 px-6 shadow-sm backdrop-blur-md">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="w-10 shrink-0" aria-hidden />
              <h2 className="min-w-0 flex-1 text-center text-xl font-semibold text-text-heading">
                {Strings.resultTitle}
              </h2>
              <button
                type="button"
                onClick={() => setLegendOpen(true)}
                className="shrink-0 rounded-full p-2 text-primary hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
                aria-label="Risk eşikleri açıklaması"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-primary text-base font-bold text-primary">
                  ?
                </span>
              </button>
            </div>
            <div className="mb-6">
              <p className="mb-1 text-center text-4xl font-bold text-primary">
                {Strings.riskPercentage.replace('{value}', result.riskPercent.toFixed(1))}
              </p>
              {result.hasDiabetesMultiplier && result.rawRiskPercent != null && (
                <p className="text-center text-xs text-text-muted">
                  (Diyabet çarpanı uygulandı: %{result.rawRiskPercent.toFixed(1)} × 1.3)
                </p>
              )}
            </div>
            <div className="flex flex-col items-center py-2">
              <RiskGauge riskPercent={result.riskPercent} compact />
            </div>
            {isPublic ? (
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={() => setRecommendationsModalOpen(true)}
                  className={`rounded-full px-8 py-3 text-base font-bold text-[#FFFFFF] transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1A1A1A]/20 ${RECOMMENDATIONS_BUTTON_CLASS[category]}`}
                  aria-label="Sağlık önerilerini göster"
                >
                  Önerileri Gör
                </button>
              </div>
            ) : (
              <p className="mt-4 text-center text-base text-text-heading">
                {Strings.riskCategory}: {categoryLabel}
              </p>
            )}
          </div>

          <p className="rounded-xl bg-card-bg px-4 py-3 text-sm leading-relaxed text-text-muted">
            {Strings.clinicalResultFooter}
          </p>

          <button
            type="button"
            onClick={onNewCalculation}
            className="w-full rounded-2xl bg-primary py-4 text-lg font-bold text-white transition hover:bg-primary-active active:bg-primary-active"
          >
            {Strings.newCalculation}
          </button>
        </div>
      </div>

      <LegendModal visible={legendOpen} onClose={() => setLegendOpen(false)} />

      <RecommendationsModal
        visible={recommendationsModalOpen}
        onClose={() => setRecommendationsModalOpen(false)}
        tier={recommendations.tier}
        title={recommendations.title}
        icon={recommendations.icon}
        bullets={recommendations.bullets}
      />
    </div>
  );
}
